import { Button, Checkbox, Icon, Modal, SearchBar } from "@legal-500/legal500-design-system";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  buildRegionNodeTree,
  regionNodeKey,
  useRegionNodes,
  type RegionNodeTreeNode,
} from "@/hooks/useRegions";
import type { RegionAssignment } from "@/api/profiles/types";
import styles from "./RegionPicker.module.css";

export type RegionsPickerProps = {
  /** Selected country nodes (multi). */
  value: RegionAssignment[];
  onChange: (regions: RegionAssignment[]) => void;
  label?: string;
  error?: string | null;
  disabled?: boolean;
  /**
   * `legacy_publications.site` domain of the profile's selected Website
   * (resolved via `WEBSITE_OPTIONS.domain`, e.g. `legal500.fr`). When set, the
   * tree is scoped to the countries that website publishes (spec 19); countries
   * not published there are hidden, and on a Website change any currently-
   * selected countries missing from the new site's tree are pruned with a note.
   * Omit for pickers with no Website control (full tree).
   */
  site?: string;
};

const NO_NAME = "—";

function nodeLabel(name: string | null | undefined): string {
  return name ?? NO_NAME;
}

/**
 * Hierarchical multi-select COUNTRY picker for PROFILES.
 *
 * The default view is a "Countries" panel showing only the currently-selected
 * countries as a removable list plus a button that opens a modal. Picking
 * countries happens inside the modal, which renders the `api_region_country_v2`
 * tree (region-parent → region → country) built off `(parentKind, parentId)`
 * with a search box and collapsible region headers.
 *
 * Profiles targeting is **country-only** (§4.2 of
 * 18-country-picker-and-entitlement-gating.md), so only `kind:"country"` leaves
 * get a checkbox and the picker emits only `{ kind:"country", id }`.
 * Region-parent and region nodes render as non-selectable, collapsible group
 * headers used purely for navigation — no checkbox, no cascade-select. Used by
 * lawyer / firm / firm-tab / article / team-page editors.
 */
export function RegionsPicker({
  value,
  onChange,
  label,
  error,
  disabled,
  site,
}: RegionsPickerProps) {
  const { nodes, loading, error: fetchError } = useRegionNodes(site);
  const tree = useMemo(() => buildRegionNodeTree(nodes), [nodes]);

  const [modalOpen, setModalOpen] = useState(false);
  const [query, setQuery] = useState("");
  const trimmedQuery = query.trim().toLowerCase();
  const searchActive = trimmedQuery.length > 0;
  const visibleTree = useMemo(() => filterTree(tree, trimmedQuery), [tree, trimmedQuery]);

  // Centralised expand/collapse state keyed by `${kind}:${id}`. Missing keys
  // default to expanded (`?? true`), so the tree starts fully expanded even
  // though it loads asynchronously.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggleExpanded = (key: string) =>
    setExpanded(prev => ({ ...prev, [key]: !(prev[key] ?? true) }));

  // Only country selections are honoured; any legacy region rows are ignored for
  // display (they're dropped on the next save — §6).
  const selectedCountries = useMemo(() => value.filter(v => v.kind === "country"), [value]);
  const selectedSet = useMemo(
    () => new Set(selectedCountries.map(v => regionNodeKey(v))),
    [selectedCountries],
  );

  // Map of node key → name so the selected-list panel can resolve readable
  // labels for the current selection.
  const nameByKey = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const n of nodes) m.set(regionNodeKey(n), n.name);
    return m;
  }, [nodes]);

  // Country ids published on the current site (spec 19). Used to prune
  // now-invalid selections when the Website changes.
  const availableCountryIds = useMemo(
    () => new Set(nodes.filter(n => n.kind === "country").map(n => n.id)),
    [nodes],
  );

  // Note shown after a Website change prunes selected countries not published on
  // the new site. Latest `value`/`onChange` are read via refs so the prune
  // effect fires only on a site/tree change, not on every parent re-render.
  const [pruneNote, setPruneNote] = useState<string | null>(null);
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const prevSiteRef = useRef(site);

  useEffect(() => {
    // Only scope when a site is set and its (non-empty) tree has loaded. An
    // empty tree means the hook is mid-fetch or fell back to the full tree
    // (spec 19 §4) — either way there's nothing to prune against.
    if (!site || loading || fetchError || nodes.length === 0) return;
    const siteChanged = prevSiteRef.current !== site;
    prevSiteRef.current = site;

    const current = valueRef.current;
    const kept = current.filter(v => v.kind !== "country" || availableCountryIds.has(v.id));
    const removed = current.length - kept.length;
    if (removed > 0) {
      setPruneNote(
        `Removed ${removed} ${removed === 1 ? "country" : "countries"} not available on ${site}`,
      );
      onChangeRef.current(kept);
    } else if (siteChanged) {
      // Switched to a site that needs no pruning — clear any stale note.
      setPruneNote(null);
    }
  }, [site, availableCountryIds, loading, fetchError, nodes.length]);

  function toggleCountry(node: RegionNodeTreeNode) {
    const key = regionNodeKey(node);
    if (selectedSet.has(key)) {
      onChange(value.filter(v => regionNodeKey(v) !== key));
    } else {
      onChange([...value, { kind: "country", id: node.id }]);
    }
  }

  function remove(target: RegionAssignment) {
    const key = regionNodeKey(target);
    onChange(value.filter(v => regionNodeKey(v) !== key));
  }

  function closeModal() {
    setModalOpen(false);
    setQuery("");
  }

  const renderNode = (node: RegionNodeTreeNode): React.ReactNode => {
    const key = regionNodeKey(node);
    const hasChildren = node.children.length > 0;
    // While a search is active every match is force-expanded; clearing the
    // search restores the user's expand/collapse state.
    const isOpen = searchActive ? true : (expanded[key] ?? true);
    const childListId = `region-children-${key}`;
    const isCountry = node.kind === "country";
    const isSelected = selectedSet.has(key);

    return (
      <li
        key={key}
        role="treeitem"
        aria-selected={isCountry ? isSelected : undefined}
        className={styles.node}
      >
        <div className={styles.row}>
          {hasChildren ? (
            <button
              type="button"
              className={styles.expandButton}
              aria-expanded={isOpen}
              aria-controls={childListId}
              aria-label={
                isOpen ? `Collapse ${nodeLabel(node.name)}` : `Expand ${nodeLabel(node.name)}`
              }
              onClick={() => toggleExpanded(key)}
            >
              <Icon icon={isOpen ? "chevron-down" : "chevron-right"} size="small" />
            </button>
          ) : (
            <span className={styles.expandSpacer} aria-hidden="true" />
          )}
          {isCountry ? (
            <Checkbox
              label={nodeLabel(node.name)}
              // The design-system Checkbox renders its visible label as an
              // unassociated sibling span, so set an explicit accessible name
              // for screen readers (and role-based selection).
              aria-label={nodeLabel(node.name)}
              checked={isSelected}
              disabled={disabled}
              onChange={() => toggleCountry(node)}
            />
          ) : (
            // Region-parent / region nodes are non-selectable group headers.
            <span className={styles.groupHeader}>{nodeLabel(node.name)}</span>
          )}
        </div>
        {hasChildren && isOpen && (
          <ul id={childListId} role="group" className={styles.children}>
            {node.children.map(renderNode)}
          </ul>
        )}
      </li>
    );
  };

  const openButtonLabel = selectedCountries.length === 0 ? "Select countries" : "Edit countries";

  return (
    <div role="group" aria-labelledby="regions-picker-label" className={styles.wrapper}>
      {label && (
        <div id="regions-picker-label" className={styles.label}>
          {label}
        </div>
      )}

      {selectedCountries.length > 0 ? (
        <ul className={styles.selectedList} aria-label="Selected countries">
          {selectedCountries.map(target => {
            const key = regionNodeKey(target);
            const name = nodeLabel(nameByKey.get(key) ?? undefined);
            return (
              <li key={key} className={styles.selectedRow}>
                <span className={styles.selectedName}>{name}</span>
                <button
                  type="button"
                  className={styles.removeButton}
                  aria-label={`Remove ${name}`}
                  disabled={disabled}
                  onClick={() => remove(target)}
                >
                  <Icon icon="cross" size="small" />
                </button>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className={styles.emptySelection}>No countries selected.</p>
      )}

      <button
        type="button"
        className={styles.openButton}
        disabled={disabled}
        onClick={() => setModalOpen(true)}
      >
        {openButtonLabel}
      </button>

      {pruneNote && (
        <p role="status" className={styles.muted}>
          {pruneNote}
        </p>
      )}
      {error && (
        <p role="alert" className={styles.errorText}>
          {error}
        </p>
      )}

      <Modal isOpen={modalOpen} onClose={closeModal} title="Select countries">
        <div className={styles.modalBody}>
          <SearchBar
            value={query}
            onChange={setQuery}
            onClear={() => setQuery("")}
            startIcon={<Icon icon="magnifier" />}
            placeholder="Search countries…"
          />
          <div className={styles.modalList}>
            {loading ? (
              <p className={styles.muted}>Loading countries…</p>
            ) : fetchError ? (
              <p role="alert" className={styles.errorText}>
                Failed to load countries: {fetchError.message}
              </p>
            ) : visibleTree.length === 0 ? (
              <p className={styles.muted}>No countries available.</p>
            ) : (
              <ul role="tree" aria-label={label ?? "Countries"} className={styles.tree}>
                {visibleTree.map(renderNode)}
              </ul>
            )}
          </div>
          <div className={styles.modalFooter}>
            <Button onClick={closeModal}>Done</Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function filterTree(nodes: RegionNodeTreeNode[], query: string): RegionNodeTreeNode[] {
  if (!query) return nodes;
  const result: RegionNodeTreeNode[] = [];
  for (const node of nodes) {
    const matched = (node.name ?? "").toLowerCase().includes(query);
    const filteredChildren = filterTree(node.children, query);
    if (matched || filteredChildren.length > 0) {
      result.push({ ...node, children: filteredChildren });
    }
  }
  return result;
}
